import React from 'react';
import type { ApiInvitation, ApiTemplate } from '@/src/types';
import { JsonTemplateRenderer } from './JsonTemplateRenderer';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';
import { CardMediaGuard, LazyInView } from './CardMediaGuard';
import { getTemplatePreview } from './templatePreviews';
import { useSignedMedia } from '@/src/infrastructure/media/useSignedMedia';

/**
 * Build a rich sample invitation so code-rendered templates have realistic
 * data to display in the card thumbnail. Wedding templates get full
 * customData so couple-name components render correctly.
 */
function sampleInvitation(template: ApiTemplate): ApiInvitation {
  const eventDate = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString();
  return {
    id: `preview-${template.id}`,
    publicSlug: null,
    templateId: template.id,
    occasionType: template.occasionType,
    status: 'PUBLISHED',
    hostedUrl: null,
    eventName: template.occasionType === 'WEDDING' ? 'Aarav & Mira' : 'Your Celebration',
    eventDate,
    eventVenue: 'The Grand Venue, Mumbai',
    hostName: 'The Host Family',
    customMessage: 'Join us for an evening of joy, stories, and celebration.',
    rsvpLink: '#',
    mediaUrls: [],
    customData:
      template.occasionType === 'WEDDING'
        ? {
            wedding: {
              groom: {
                fullName: 'Aarav Sharma',
                nickname: 'Aarav',
                profession: 'Architect',
                fatherName: 'Amit Sharma',
                motherName: 'Neelam Sharma',
                about: 'Thoughtful, warm, and happiest when surrounded by family and music.',
              },
              bride: {
                fullName: 'Mira Mehta',
                nickname: 'Mira',
                profession: 'Designer',
                fatherName: 'Sanjay Mehta',
                motherName: 'Kavita Mehta',
                about: 'Graceful, curious, and known for making every gathering feel intimate.',
              },
              weddingEvents: [
                {
                  title: 'Mehendi',
                  date: new Date(Date.now() + 43 * 24 * 60 * 60 * 1000).toISOString(),
                  startTime: '05:00 PM',
                  venue: 'Courtyard Garden',
                  description: 'An evening of henna, music, and family laughter.',
                },
                {
                  title: 'Wedding',
                  date: eventDate,
                  startTime: '07:30 PM',
                  venue: 'The Grand Venue, Mumbai',
                  description: 'The ceremony, blessings, dinner, and celebration.',
                },
              ],
            },
          }
        : undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Renders a card-sized thumbnail preview for a template.
 *
 * Priority order:
 * 1. Registry-backed code templates → live scaled-down TemplateRenderer.
 *    CSS `transform: scale()` creates a new containing block for
 *    `position:fixed` descendants (e.g. Lagna Patrika's opening gate),
 *    so fixed overlays are correctly contained within the card bounds.
 * 2. JSON-schema templates → live scaled-down JsonTemplateRenderer.
 *    Live renders (1, 2) mount lazily near the viewport and inside CardMediaGuard, which
 *    strips autoplay/src from any <video>/<audio> so a card never downloads or plays media.
 *    Media-heavy journeys skip the live render entirely (STATIC_POSTER_CARD_TEMPLATE_IDS).
 * 3. API-served templates with a previewImageUrl → static image overlay card.
 * 4. Generic labelled fallback.
 */
const PLOT_PLANE_POSTER = '/assets/templates/exp-wedding-plot-plane-3d/01-couple-sangeet-wide.jpg';

/**
 * Media-heavy code templates that must NOT be mounted live inside a catalog card.
 *
 * Each of these mounts every scene's background <video> (and audio) as soon as it
 * renders. The wizard's Wedding step lists ~26 templates at once, so mounting them
 * live made Step 2 fetch ~150 MB of mp4/mp3 (Kirtan Divine alone ~107 MB) and decode
 * ~34 videos in parallel, freezing the page on laptops and phones. Cards for these
 * show a static poster instead — the recorded preview poster (public/template-previews,
 * see `npm run record:previews`) when one exists, else `previewImageUrl`; the full live render
 * is still available from the Preview dialog and on the published invitation.
 */
export const STATIC_POSTER_CARD_TEMPLATE_IDS: ReadonlySet<string> = new Set([
  'video-01-royal-gold',
  'video-02-kirtan-divine',
  'video-03-radha-krishna',
  'royal-ganesh-interactive',
  'exp-wedding-indo-ghibli-v1',
  'exp-wedding-pichwai-v1',
  'exp-wedding-storybook-3d-v1',
]);

function posterFor(template: ApiTemplate): string | null | undefined {
  return (
    getTemplatePreview(template.id)?.poster ??
    (template.previewImageUrl || registry.get(template.id)?.metadata.cardPosterUrl)
  );
}

/** Registry templates that declare a `cardPosterUrl` (e.g. WebGL experiences) never mount live in a card. */
function hasRegistryCardPoster(templateId: string): boolean {
  return Boolean(registry.get(templateId)?.metadata.cardPosterUrl);
}

function StaticPosterCard({ template, src }: { template: ApiTemplate; src?: string | null }) {
  const media = useSignedMedia(src ?? template.previewImageUrl ?? undefined);
  return (
    <div className="relative h-full w-full overflow-hidden bg-[#11130f] text-[#fffaf0]">
      <img
        src={media.src}
        onError={media.onError}
        alt={template.name}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6 text-[#faf6ef]">
        <p className="text-[0.56rem] font-semibold uppercase tracking-[0.3em] text-[#ecdcc1]">
          {template.occasionType.replace(/_/g, ' ')}
        </p>
        <h3 className="mt-2 font-serif text-[1.5rem] font-bold leading-tight">{template.name}</h3>
      </div>
    </div>
  );
}

/** Lightweight stand-in shown until a live card scrolls near the viewport. */
function CardPlaceholder({ template }: { template: ApiTemplate }) {
  const poster = posterFor(template);
  if (poster) return <StaticPosterCard template={template} src={poster} />;
  return <div data-testid="card-placeholder" className="h-full w-full animate-pulse bg-surface-low" />;
}

function TemplateCardPreviewContent({ template }: { template: ApiTemplate }) {
  // WebGL journeys must not mount a canvas inside every catalog card.
  if (template.id === 'exp-wedding-plot-plane-3d') {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#140a07] text-[#fffaf0]">
        <img
          src={template.previewImageUrl || PLOT_PLANE_POSTER}
          alt={template.name}
          className="absolute inset-0 h-full w-full object-cover object-[center_32%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 text-[#faf6ef]">
          <p className="font-serif text-lg leading-tight">Aisha &amp; Kabir</p>
          <p className="mt-1 text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-[#e8c872]">
            {template.name}
          </p>
        </div>
      </div>
    );
  }

  // Video/audio-backed journeys and WebGL templates with a registry `cardPosterUrl`:
  // static poster only (see STATIC_POSTER_CARD_TEMPLATE_IDS).
  if (STATIC_POSTER_CARD_TEMPLATE_IDS.has(template.id) || hasRegistryCardPoster(template.id)) {
    const poster = posterFor(template);
    if (poster) return <StaticPosterCard template={template} src={poster} />;
  }

  // --- 1. Live preview for all registry-backed code templates ---
  // Mounted only near the viewport (LazyInView) and with all media neutralised (CardMediaGuard).
  if (registry.has(template.id)) {
    return (
      <LazyInView placeholder={<CardPlaceholder template={template} />}>
        <CardMediaGuard>
          <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none" inert>
            <TemplateRenderer templateId={template.id} data={sampleInvitation(template)} />
          </div>
        </CardMediaGuard>
      </LazyInView>
    );
  }

  // --- 2. Experience / AI-native templates ---
  // Same guards as the other live branches: mounted near the viewport, media neutralised.
  if (
    template.renderMode === 'experience' ||
    template.id.startsWith('exp-') ||
    (template.capabilities as { experience?: boolean } | undefined)?.experience === true
  ) {
    const manifest = resolveExperienceManifest(template.id, sampleInvitation(template));
    if (manifest) {
      return (
        <LazyInView placeholder={<CardPlaceholder template={template} />}>
          <CardMediaGuard>
            <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none overflow-hidden" inert>
              <SceneExperienceRenderer manifest={manifest} preview />
            </div>
          </CardMediaGuard>
        </LazyInView>
      );
    }
  }

  // --- 3. JSON-schema templates ---
  if (template.renderMode === 'json-renderer' && template.layoutSchema) {
    return (
      <LazyInView placeholder={<CardPlaceholder template={template} />}>
        <CardMediaGuard>
          <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none" inert>
            <JsonTemplateRenderer
              schema={template.layoutSchema}
              theme={template.themeConfig}
              data={sampleInvitation(template)}
              mode="preview"
            />
          </div>
        </CardMediaGuard>
      </LazyInView>
    );
  }

  // --- 4. API-served templates with a static preview image ---
  if (template.previewImageUrl) {
    return <StaticPosterCard template={template} />;
  }

  // --- 5. Generic labelled fallback ---
  return <LabelledFallbackCard template={template} />;
}

function LabelledFallbackCard({ template }: { template: ApiTemplate }) {
  return (
    <div
      className="w-full h-full flex items-center justify-center text-center px-8"
      style={{ background: 'linear-gradient(135deg, #fcf8ff, #e8d5c4)', color: '#574eb1' }}
    >
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold">
          {template.occasionType.replace(/_/g, ' ')}
        </p>
        <p className="mt-3 text-2xl font-bold leading-tight">{template.name}</p>
      </div>
    </div>
  );
}

/**
 * A thumbnail renders a real template with sample data — if that template
 * throws (e.g. a null-deref on sample data it doesn't expect), React would
 * unmount the WHOLE page (this blanked /designs). Contain it to the one card.
 */
class PreviewErrorBoundary extends React.Component<
  { template: ApiTemplate; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.warn(`TemplateCardPreview: "${this.props.template.id}" failed to render its preview — showing fallback.`, error);
  }

  render() {
    return this.state.failed ? <LabelledFallbackCard template={this.props.template} /> : this.props.children;
  }
}

export function TemplateCardPreview({ template }: { template: ApiTemplate }) {
  return (
    <PreviewErrorBoundary key={template.id} template={template}>
      <TemplateCardPreviewContent template={template} />
    </PreviewErrorBoundary>
  );
}
